// Elv3Stepper.jsx
import React, { useState, useEffect } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import XRMPanel from "./XRM/XRMPanel";
import { Elv3FormDetails } from "./Elv3Form/Elv3FormDetails";
import Elv3ReviewSubmit from "./Elv3ReviewSubmit";
import DefectCreationStep from "./Elv3Form/DefectCreationStep";
import { useHeaders } from "../HeadersContext";
import DefectCreationStepTest from "./Elv3Form/DefectCreationStepTest";

const steps = [
  "Login To DOB",
  "Enter Inspection And Device Details",
  "Review And Submit Inspection",
  "Create Defects (Optional)",
];

const Elv3Stepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoginSuccessful, setIsLoginSuccessful] = useState(false);
  const [loginResponse, setLoginResponse] = useState(null);
  const [finalJSON, setFinalJSON] = useState(null);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [defectSubmissionResult, setDefectSubmissionResult] = useState(null);
  const { headers, setHeaders } = useHeaders();
  const [stepErrors, setStepErrors] = useState({});
  const [showDefectPrompt, setShowDefectPrompt] = useState(false);
  const [skipDefectCreation, setSkipDefectCreation] = useState(false);

  const handleLoginSuccess = (data) => {
    setLoginResponse(data);
    setIsLoginSuccessful(true);
    handleNext();
  };

  const handleNext = () => {
    // If we're about to go to defect creation step, ask if they want to create defects
    if (activeStep === 2 && submissionResponse && !skipDefectCreation) {
      setShowDefectPrompt(true);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsLoginSuccessful(false);
    setFinalJSON(null);
    setSubmissionResponse(null);
    setSelectedDevices([]);
    setDefectSubmissionResult(null);
    setStepErrors({});
    setSkipDefectCreation(false);
  };

  const handleDefectChoice = (createDefects) => {
    setShowDefectPrompt(false);
    if (createDefects) {
      setActiveStep(3); // Go to defect creation
    } else {
      setSkipDefectCreation(true);
      setActiveStep(4); // Skip to completion
    }
  };

  useEffect(() => {
    if (isLoginSuccessful && loginResponse?.contact?.length > 0) {
      const [contact] = loginResponse.contact;
      setHeaders({
        authtoken: contact.AuthToken,
        systemuser: contact.ContactID,
        businessAddress: contact.BusinessAddress,
        city: contact.City,
        state: contact.State,
        zip: contact.Zip,
      });
    }
  }, [isLoginSuccessful, loginResponse, setHeaders]);

  // Extract and prepare device information when finalJSON is set
  useEffect(() => {
    if (finalJSON && finalJSON.Elv3List && Array.isArray(finalJSON.Elv3List)) {
      console.log("Processing devices from finalJSON:", finalJSON.Elv3List);

      // Map each device in the Elv3List to the format needed for DefectCreationStep
      const devices = finalJSON.Elv3List.map((device) => {
        return {
          DeviceID: device.DeviceID,
          DeviceTypeLabel: device.DeviceTypeLabel || "Elevator",
          DeviceName: `Device ${device.DeviceID}`,
          InspectionType: device.InspectionType,
          Status: "Active",
        };
      });

      console.log("Prepared devices for defect creation:", devices);
      setSelectedDevices(devices);
    }
  }, [finalJSON]);

  // Update selected devices when submission is successful to include elv3GroupGuid
  useEffect(() => {
    if (submissionResponse && submissionResponse.elv3GroupGuid && selectedDevices.length > 0) {
      const updatedDevices = selectedDevices.map((device) => ({
        ...device,
        elv3GroupGuid: submissionResponse.elv3GroupGuid,
      }));
      setSelectedDevices(updatedDevices);
    }
  }, [submissionResponse, selectedDevices]);

  const handleDefectsSubmit = async (defects) => {
    try {
      setDefectSubmissionResult(defects);
      setActiveStep(4); // Move to completion step
    } catch (error) {
      console.error("Error submitting defects:", error);
      setStepErrors({
        ...stepErrors,
        3: `Error submitting defects: ${
          error.message || "Unknown error occurred"
        }`,
      });
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <XRMPanel onLoginSuccess={handleLoginSuccess} />;
      case 1:
        return (
          <Elv3FormDetails
            onFinalJSONGenerated={(json) => {
              console.log("Final JSON received:", json);
              setFinalJSON(json);
              handleNext();
            }}
          />
        );
      case 2:
        return finalJSON ? (
          <Elv3ReviewSubmit
            finalJSON={finalJSON}
            onSubmitSuccess={(res) => {
              console.log("Submission result:", res);
              setSubmissionResponse(res);
              handleNext();
            }}
          />
        ) : (
          <Typography>
            No final JSON available. Please submit the form in the previous
            step.
          </Typography>
        );
      case 3:
        return submissionResponse && selectedDevices.length > 0 ? (
          <DefectCreationStep
            selectedDevices={selectedDevices}
            elv3Guid={submissionResponse?.elv3GroupGuid}
            onSubmit={handleDefectsSubmit}
          />
        ) : (
          <div className="p-6 bg-white">
            <Alert severity="error" sx={{ mb: 2 }}>
              {!submissionResponse
                ? "Please complete the ELV3 submission first."
                : "No devices were found in the inspection submission."}
            </Alert>
            <Typography variant="body1">
              You need to select devices in the "Enter Inspection And Device
              Details" step and complete the ELV3 submission before creating
              defects.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBack}
              sx={{ mt: 2 }}
            >
              Go Back
            </Button>
          </div>
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  const getStepValidation = (step) => {
    // Return true if the step can be advanced
    switch (step) {
      case 0:
        return isLoginSuccessful;
      case 1:
        return !!finalJSON;
      case 2:
        return !!submissionResponse;
      case 3:
        return true; // Always allow completing the flow
      default:
        return false;
    }
  };

  // Custom stepper label styling to handle completed steps differently
  const getStepLabelProps = (index) => {
    if (index === 3) {
      // Defect creation step
      return {
        optional: <Typography variant="caption">(Optional)</Typography>,
        sx: skipDefectCreation ? { color: "text.disabled" } : {},
      };
    }
    return {};
  };

  return (
    <div>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel {...getStepLabelProps(index)}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div>
        {activeStep === steps.length ? (
          <div className="p-8 bg-white rounded-lg shadow-md">
            <Alert severity="success" className="mb-4">
              ELV3 inspection has been successfully submitted!
              {defectSubmissionResult
                ? " Defects have also been recorded."
                : ""}
            </Alert>

            <div className="mb-4">
              <Typography variant="h6" gutterBottom>
                Submission Summary
              </Typography>
              <Typography variant="body2">
                • ELV3 Group GUID: {submissionResponse?.elv3GroupGuid}
              </Typography>
              <Typography variant="body2">
                • Devices processed: {selectedDevices.length}
              </Typography>
              {defectSubmissionResult && (
                <Typography variant="body2">
                  • Defects submitted:{" "}
                  {
                    Object.values(defectSubmissionResult)
                      .flat()
                      .filter(
                        (d) =>
                          d.elevatorPart &&
                          d.violationCondition &&
                          d.suggestedRemedy
                      ).length
                  }
                </Typography>
              )}
              {skipDefectCreation && (
                <Typography variant="body2">
                  • No defects were created as per your choice
                </Typography>
              )}
            </div>

            <Button
              variant="contained"
              color="primary"
              onClick={handleReset}
              className="mt-4"
            >
              Start New Submission
            </Button>
          </div>
        ) : (
          <div>
            <div className="bg-gradient-to-br from-gray-300 to-slate-400 border-gray-300 border rounded-xl w-fit my-4 min-w-[800px]">
              {stepErrors[activeStep] && (
                <Alert severity="error" className="m-4">
                  {stepErrors[activeStep]}
                </Alert>
              )}
              {getStepContent(activeStep)}
            </div>
            <div className="mt-4 flex justify-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!getStepValidation(activeStep)}
              >
                {activeStep === steps.length - 1 ? "Finish" : "Next"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Dialog to ask if user wants to create defects */}
      <Dialog
        open={showDefectPrompt}
        onClose={() => setShowDefectPrompt(false)}
      >
        <DialogTitle>Create Defects?</DialogTitle>
        <DialogContent>
          <Typography>
            Your ELV3 inspection has been successfully created with GUID:
            <Box
              component="span"
              fontWeight="bold"
              sx={{ display: "block", my: 1 }}
            >
              {submissionResponse?.elv3GroupGuid}
            </Box>
            Would you like to create defects for this inspection?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, fontWeight: "medium" }}>
            You have {selectedDevices.length} device(s) available for defect
            creation:
          </Typography>
          <ul className="mt-2 pl-6 list-disc">
            {selectedDevices.map((device, index) => (
              <li key={index}>
                Device ID: {device.DeviceID} ({device.DeviceTypeLabel || "Elevator"})
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDefectChoice(false)} color="primary">
            Skip Defect Creation
          </Button>
          <Button
            onClick={() => handleDefectChoice(true)}
            variant="contained"
            color="primary"
          >
            Create Defects
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Elv3Stepper;
